import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getGazeReport,
  getGazeReportTrends,
} from "../../../../services/gazeReportApi";
import { buildGazeReportSrcDoc } from "./iframeTemplate";
import { build as buildGazeReportData } from "./gazeAdapter";
import {
  normalizeGazeReportResponse,
  normalizeGazeTrendsResponse,
} from "./gazeReportData";
import { useT } from "../../../../i18n/useT";
import { useLocale } from "../../../../i18n/I18nProvider";
import { openExternalUrl } from "../../../../utlis/webViewBridge";
import { isAllowedGazeSurveyUrl } from "./gazeReportSurvey";
import "./GazeReport.scss";

const errorPresentation = (error) => {
  const status = error?.response?.status;
  if (status === 401) {
    return {
      titleKey: "expired_title",
      messageKey: "expired_reopen_desc",
      retryable: false,
      unauthorized: true,
    };
  }
  if (status === 403) {
    return {
      titleKey: "unavailable_title",
      messageKey: "unavailable_desc",
      retryable: false,
      unauthorized: true,
    };
  }
  if (status === 404) {
    return {
      titleKey: "not_found_title",
      messageKey: "not_found_desc",
      retryable: false,
    };
  }
  if (status === 410) {
    return {
      titleKey: "expired_title",
      messageKey: "expired_new_desc",
      retryable: false,
      unauthorized: true,
    };
  }
  if (status === 429) {
    return {
      titleKey: "busy_title",
      messageKey: "busy_desc",
      retryable: true,
    };
  }
  return {
    titleKey: "load_failed_title",
    messageKey: "load_failed_desc",
    retryable: true,
  };
};

const createFrameNonce = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const GazeReport = ({ request, onUnauthorized, onBack }) => {
  const t = useT("gazeReport");
  const { locale } = useLocale();
  const iframeRef = useRef(null);
  const frameNonceRef = useRef(createFrameNonce());
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: "loading", input: null });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ status: "loading", input: null });

    // Trends are optional: if they fail or are invalid the report still
    // renders, just without the performance trend.
    const trends = getGazeReportTrends({
      ...request,
      signal: controller.signal,
    })
      .then((response) => normalizeGazeTrendsResponse(response, request))
      .catch(() => null);

    Promise.all([
      getGazeReport({ ...request, signal: controller.signal }),
      trends,
    ])
      .then(([response, trendSessions]) => {
        if (!active) return;
        const input = normalizeGazeReportResponse(response, request);
        setState({ status: "ready", input: { ...input, trendSessions } });
      })
      .catch((error) => {
        if (!active || error?.code === "ERR_CANCELED") return;
        const presentation = errorPresentation(error);
        if (presentation.unauthorized) onUnauthorized?.();
        setState({ status: "error", error: presentation, input: null });
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [attempt, onUnauthorized, request]);

  const srcDoc = useMemo(() => {
    if (state.status !== "ready" || !state.input) return "";
    return buildGazeReportSrcDoc(
      buildGazeReportData(state.input),
      locale,
      frameNonceRef.current,
    );
  }, [state, locale]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.frameNonce !== frameNonceRef.current) return;
      if (event.data?.type === "seniorlife:gaze-report-back") {
        onBack?.();
      } else if (
        event.data?.type === "seniorlife:gaze-report-survey" &&
        isAllowedGazeSurveyUrl(event.data?.url)
      ) {
        openExternalUrl(event.data.url);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onBack]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  if (state.status === "loading") {
    return (
      <main className="gaze-report-state" role="status" aria-live="polite">
        <div className="gaze-report-spinner" aria-hidden="true" />
        <h1>{t("loading_title")}</h1>
        <p>{t("loading_desc")}</p>
      </main>
    );
  }

  if (state.status === "error") {
    return (
      <main className="gaze-report-state" role="alert">
        <h1>{t(state.error.titleKey)}</h1>
        <p>{t(state.error.messageKey)}</p>
        {state.error.retryable && (
          <button type="button" onClick={retry} autoFocus>
            {t("try_again")}
          </button>
        )}
      </main>
    );
  }

  return (
    <main className="gaze-report-wrapper">
      <iframe
        ref={iframeRef}
        className="gaze-report-iframe"
        title={t("iframe_title")}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
      />
    </main>
  );
};

export default GazeReport;
